import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory, useLocation, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import CIcon from "@coreui/icons-react";
import {getApi} from "../../apis/backendApis";
import useLoading from "../../services/useLoading";
import useAlert from "../../services/useAlert";
import {States} from "../../store/ducks/rootReducer";
import {
    COR_SITUACAO,
    EtapaProcesso,
    Evento,
    ItemProcesso,
    Processo,
    SITUACAO_ETAPA,
    SituacaoEvento,
    SituacaoItem,
    SituacaoProcesso,
    TIPO_PAPEL_CLIENTE,
    dataBr,
    dataCurta,
    erroDaApi,
    eventosDaEtapa,
    eventosDoItem,
    hojeIso,
    proximoAgendado,
    quandoEvento,
    resolvido,
    textoDesvio,
} from "./modelo";
import ItemPainel from "./itemPainel";
import {DocumentosAba, HistoricoAba, ResponsaveisAba} from "./abas";
import {EtapaModal, ReprogramarEtapaModal, SituacaoModal} from "./modais";
import CronogramaAba from "./cronogramaAba";
import AgendaAba, {NovoAgendamento} from "./agendaAba";
import EventoModal from "./eventoModal";
import {COR_EVENTO} from "./calendario";
import useMidia, {DESKTOP} from "./useMidia";
import '../../scss/styles-processo.scss';

type Aba = 'etapas' | 'cronograma' | 'agenda' | 'responsaveis' | 'historico' | 'documentos';
type Filtro = 'todos' | 'meus' | 'pendentes' | 'duvidas' | 'revisar' | 'cliente' | 'atrasados' | 'agendados';

export type Executar = (rota: string, dados?: any, arquivos?: File[]) => Promise<boolean>;

const FILTROS: Array<[Filtro, string]> = [
    ['meus', 'Minhas'],
    ['todos', 'Todos'],
    ['pendentes', 'Pendentes'],
    ['duvidas', 'Dúvidas'],
    ['revisar', 'Revisar'],
    ['cliente', 'Do cliente'],
    ['atrasados', 'Atrasados'],
    ['agendados', 'Agendados'],
];

/** Filtros que so aparecem quando tem algo (ou estao escolhidos). */
const SO_COM_ITENS: Filtro[] = ['duvidas', 'revisar', 'agendados'];

const ICONE_SITUACAO: {[s: number]: string} = {
    [SituacaoItem.PENDENTE]: 'cil-circle',
    [SituacaoItem.EM_ANDAMENTO]: 'cil-media-play',
    [SituacaoItem.AGUARDANDO_CLIENTE]: 'cil-user',
    [SituacaoItem.CONCLUIDO]: 'cil-check-circle',
    [SituacaoItem.DECLINADO]: 'cil-ban',
};

/** Itens em sequencia agrupados pelo grupo (subfase) do fluxo. */
const porGrupo = (itens: ItemProcesso[]): Array<[string | null, ItemProcesso[]]> => {
    const grupos: Array<[string | null, ItemProcesso[]]> = [];
    itens.forEach(i => {
        const chave = i.grupo_nome ? `${i.grupo_codigo} ${i.grupo_nome}` : null;
        const ultimo = grupos[grupos.length - 1];
        if (ultimo && ultimo[0] === chave) {
            ultimo[1].push(i);
        } else {
            grupos.push([chave, [i]]);
        }
    });
    return grupos;
};

const ABAS: Array<[Aba, string]> = [['etapas', 'Etapas'], ['cronograma', 'Cronograma'], ['agenda', 'Agenda'], ['responsaveis', 'Responsáveis'], ['historico', 'Histórico'], ['documentos', 'Documentos']];

/**
 * Execucao do processo (#480058). Desktop: tres colunas (etapas | itens |
 * painel do item ou resumo da etapa), cada uma rolando por si. Celular:
 * etapas em sanfona e o painel do item cobrindo a tela.
 *
 * Dois filtros que se somam (#484909): a etapa escolhida no trilho (clicar de
 * novo, ou "Todas as etapas", desmarca) e o filtro dos itens, que abre em
 * "Minhas" (os itens em que o usuario age, a mesma regra do Meus Processos).
 * Com a etapa marcada, "Minhas" mostra so os meus daquela etapa; todos os
 * itens do processo so com a etapa desmarcada. No celular, a etapa e a
 * sanfona e o filtro vale para todas.
 *
 * Na URL, ?item= abre direto no item, ?etapa= na etapa, ?aba= numa aba e
 * ?evento= no evento (na aba Agenda); o state "voltar" diz para onde o voltar
 * leva.
 *
 * Agenda (#484909): o item e a etapa se agendam daqui (painel do item, resumo
 * e cabecalho da etapa, Gantt e aba Agenda), todos no mesmo modal de evento.
 */
const ProcessoTela = () => {
    const {id} = useParams<{id: string}>();
    const history = useHistory();
    const location = useLocation<{voltar?: string} | undefined>();
    const [setLoading] = useLoading();
    const [showAlert] = useAlert();
    const usuarioAtual = useSelector((state: States) => state.login.data?.user);
    const desktop = useMidia(DESKTOP);

    const [processo, setProcesso] = useState<Processo | null>(null);
    const [falhou, setFalhou] = useState<string | null>(null);
    const [aba, setAba] = useState<Aba>('etapas');
    const [filtro, setFiltro] = useState<Filtro>('todos');
    const [abertas, setAbertas] = useState<Set<number>>(new Set());
    const [etapaId, setEtapaId] = useState<number | null>(null);
    const [itemId, setItemId] = useState<number | null>(null);
    const [situacaoAberta, setSituacaoAberta] = useState(false);
    const [etapaModal, setEtapaModal] = useState<{etapa: EtapaProcesso, modo: 'transferir' | 'avulso'} | null>(null);
    const [usuarios, setUsuarios] = useState<Array<{id: number, nome: string}>>([]);
    const [versaoHistorico, setVersaoHistorico] = useState(0);
    const [reprogramando, setReprogramando] = useState<EtapaProcesso | null>(null);
    // Aberto pela URL num item: rola a lista ate ele depois de desenhar.
    const rolarAteItem = useRef(false);
    // Modal do evento: um existente ou um novo (do item, da etapa ou do calendario).
    const [agendamento, setAgendamento] = useState<({evento: Evento | null} & NovoAgendamento) | null>(null);

    const carregar = useCallback(() => {
        setLoading(true);
        return getApi().get(`processo/get/${id}`)
            .then(resposta => {
                setProcesso(resposta.data);
                setFalhou(null);
                return resposta.data as Processo;
            })
            .catch(erro => {
                setFalhou(erroDaApi(erro, 'Não foi possível carregar o processo.'));
                return null;
            })
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        const pedido = new URLSearchParams(location.search);
        carregar().then(dados => {
            if (!dados) return;
            const minha = (i: ItemProcesso) => i.quem_age === usuarioAtual?.id && !resolvido(i);
            const comMinhas = (etapa?: EtapaProcesso) => !!etapa && etapa.itens.some(minha);
            const itemPedido = Number(pedido.get('item'));
            const doItem = itemPedido ? dados.etapas.find(e => e.itens.some(i => i.id === itemPedido)) : undefined;
            const alvo = doItem?.itens.find(i => i.id === itemPedido);
            const etapaPedida = dados.etapas.find(e => e.id === Number(pedido.get('etapa')));
            const atual = dados.etapas.find(e => e.id === dados.etapa_atual_id);
            // A etapa marcada: a do item ou a pedida; senao a atual, se tiver algo meu, ou a primeira que tiver.
            const inicial = doItem ?? etapaPedida ?? (comMinhas(atual) ? atual : dados.etapas.find(comMinhas)) ?? atual ?? dados.etapas[0];
            setEtapaId(inicial?.id ?? null);
            if (inicial) {
                setAbertas(new Set([inicial.id]));
            }
            // "Minhas" sempre primeiro; so o item pedido que nao e meu (ou a etapa pedida sem nada meu) abre em "Todos".
            setFiltro(alvo ? (minha(alvo) ? 'meus' : 'todos') : (etapaPedida && !comMinhas(etapaPedida) ? 'todos' : 'meus'));
            setItemId(alvo?.id ?? null);
            rolarAteItem.current = !!alvo;
            const abaPedida = ABAS.find(([chave]) => chave === pedido.get('aba'));
            const eventoPedido = (dados.eventos || []).find(e => e.id === Number(pedido.get('evento')));
            setAba(eventoPedido ? 'agenda' : (abaPedida ? abaPedida[0] : 'etapas'));
            setAgendamento(eventoPedido ? {evento: eventoPedido} : null);
        });
        getApi().get('/usuarios/suggest').then(resposta => {
            setUsuarios((resposta.data.usuarios || []).map((par: any) => {
                const [idUsuario, nome] = Object.entries(par)[0];
                return {id: Number(idUsuario), nome: String(nome)};
            }));
        }).catch(() => setUsuarios([]));
    }, [id, location.search]);

    useEffect(() => {
        if (rolarAteItem.current && itemId !== null) {
            rolarAteItem.current = false;
            document.querySelector('.pr-item.selecionado')?.scrollIntoView({block: 'center'});
        }
    }, [itemId]);

    /**
     * POST de movimento: "data" em JSON (e arquivos, se houver). A API devolve
     * o processo atualizado; sem ele, recarrega.
     */
    const executar: Executar = (rota, dados = {}, arquivos = []) => {
        const corpo = new FormData();
        corpo.append('data', JSON.stringify(dados));
        arquivos.forEach((arquivo, i) => corpo.append(`arquivo${i}`, arquivo));
        setLoading(true);
        return getApi().post(rota, corpo)
            .then(resposta => {
                showAlert({msg: resposta.data.msg ?? 'Feito.', type: 'success', toaster: true});
                if (resposta.data.processo) {
                    setProcesso(resposta.data.processo);
                } else {
                    carregar();
                }
                setVersaoHistorico(v => v + 1);
                return true;
            })
            .catch(erro => {
                showAlert({title: 'Não foi possível', msg: erroDaApi(erro, 'Erro ao processar.'), type: 'danger', toaster: true});
                return false;
            })
            .finally(() => setLoading(false));
    };

    const recarregar = () => {
        carregar();
        setVersaoHistorico(v => v + 1);
    };

    const papeis = useMemo(() => new Map((processo?.papeis || []).map(p => [p.id, p])), [processo]);

    const item = useMemo(() => {
        if (!processo || itemId === null) return null;
        for (const etapa of processo.etapas) {
            const achado = etapa.itens.find(i => i.id === itemId);
            if (achado) return {etapa, item: achado};
        }
        return null;
    }, [processo, itemId]);

    if (falhou) {
        return <div className="pr"><div className="pr-vazio">{falhou}</div></div>;
    }
    if (!processo) {
        return null;
    }

    const ativo = processo.situacao === SituacaoProcesso.ABERTO || processo.situacao === SituacaoProcesso.EXECUTANDO;
    // Etapa marcada no trilho (desktop): filtra junto com o filtro dos itens; null = todas as etapas.
    const etapaSel = desktop ? processo.etapas.find(e => e.id === etapaId) ?? null : null;
    const escopo = etapaSel ? [etapaSel] : processo.etapas;
    // Os itens em que o usuario age (o item do cliente conta para quem acompanha o cliente).
    const meus = (i: ItemProcesso) => i.quem_age === usuarioAtual?.id;
    const passa = (f: Filtro, i: ItemProcesso) => {
        switch (f) {
            case 'meus': return meus(i) && !resolvido(i);
            case 'pendentes': return !resolvido(i);
            case 'duvidas': return i.duvidas.length > 0;
            case 'revisar': return i.em_revisao;
            case 'cliente': return i.do_cliente;
            case 'atrasados': return i.atrasado;
            case 'agendados': return !!proximoAgendado(eventosDoItem(processo, i.id));
            default: return true;
        }
    };
    const passaFiltro = (i: ItemProcesso) => passa(filtro, i);
    const conta = (etapas: EtapaProcesso[], f: Filtro = filtro) => etapas.reduce((s, e) => s + e.itens.filter(i => passa(f, i)).length, 0);
    // As contagens dos filtros sao da etapa marcada (ou de todas).
    const contagem = FILTROS.reduce((total, [f]) => ({...total, [f]: conta(escopo, f)}), {} as {[f in Filtro]: number});
    const rotuloFiltro = FILTROS.find(f => f[0] === filtro)?.[1] ?? '';

    const nomePessoa = (papelId: number) => {
        const papel = papeis.get(papelId);
        return papel ? (papel.pessoa || `${papel.nome} (sem responsável)`) : '';
    };

    const podeAgirNaEtapa = (etapa: EtapaProcesso) => {
        const papel = papeis.get(etapa.papel_id);
        return ativo && (processo.pode_gerir || (!!papel?.usuario && papel.usuario.id === usuarioAtual?.id));
    };

    const podeReprogramar = (etapa: EtapaProcesso) => podeAgirNaEtapa(etapa);

    // Agenda: gestor, master ou quem ocupa algum papel (a API confere de novo).
    const podeAgendar = ativo && (processo.pode_gerir || processo.papeis.some(p => !!p.usuario && p.usuario.id === usuarioAtual?.id));
    const agendar = (novo: NovoAgendamento) => setAgendamento({evento: null, ...novo});

    // Portal do cliente (#484909): quem participa ve o portal como o cliente, so leitura, em outra aba.
    const participa = processo.pode_gerir || processo.papeis.some(p => !!p.usuario && p.usuario.id === usuarioAtual?.id);
    const abrePortal = () => {
        // A aba abre no clique (bloqueador de pop-up); o endereco chega depois.
        const aba = window.open('', '_blank');
        getApi().post(`processo/portal/${processo.id}`, new FormData())
            .then(resposta => {
                const url = `${window.location.origin}${window.location.pathname}#/portal?sessao=${encodeURIComponent(resposta.data.sessao)}&processo=${processo.id}`;
                if (aba) {
                    aba.opener = null;
                    aba.location.href = url;
                } else {
                    window.location.href = url;
                }
            })
            .catch(erro => {
                if (aba) aba.close();
                showAlert({title: 'Não foi possível', msg: erroDaApi(erro, 'Erro ao abrir o portal do cliente.'), type: 'danger', toaster: true});
            });
    };
    const abreEvento = (evento: Evento) => setAgendamento({evento});

    /** Vai para a aba Etapas com o item aberto (do cronograma ou da agenda): na etapa dele, com o filtro se ele passar. */
    const abreItem = (idItem: number) => {
        const dona = processo.etapas.find(e => e.itens.some(i => i.id === idItem));
        const alvo = dona?.itens.find(i => i.id === idItem);
        if (dona) {
            setEtapaId(dona.id);
            setAbertas(new Set(abertas).add(dona.id));
        }
        if (alvo && !passa(filtro, alvo)) setFiltro('todos');
        setAba('etapas');
        setItemId(idItem);
    };

    /** Trilho: marca a etapa (filtra a lista); clicar na marcada, ou em "Todas as etapas", desmarca. O filtro continua. */
    const alternaEtapa = (etapa: EtapaProcesso | null) => {
        const nova = etapa && etapa.id !== etapaSel?.id ? etapa.id : null;
        setEtapaId(nova);
        if (nova !== null && item && item.etapa.id !== nova) setItemId(null);
    };

    // ------------------------------------------------------------------ pecas

    const renderItem = (i: ItemProcesso) => {
        const classe = resolvido(i) ? 'resolvido' : i.atrasado ? 'atrasado' : i.vencendo ? 'vencendo' : '';
        const agendado = proximoAgendado(eventosDoItem(processo, i.id));
        return (
            <button type="button" key={i.id} className={`pr-item ${classe} sit-${i.situacao} ${i.duvidas.length ? 'com-duvida' : ''} ${itemId === i.id ? 'selecionado' : ''}`} onClick={() => setItemId(i.id)}>
                <CIcon name={ICONE_SITUACAO[i.situacao]} className="pr-item-icone"/>
                <span className="pr-item-texto">
                    <span className="pr-item-titulo">{i.titulo}</span>
                    <span className="pr-item-meta">
                        <span>{nomePessoa(i.papel_id)}</span>
                        {!resolvido(i) && <span className="pr-item-prazo">{i.atrasado ? 'atrasado · ' : ''}{dataCurta(i.prazo)}</span>}
                        {i.situacao === SituacaoItem.DECLINADO && <span>declinado</span>}
                        {i.duvidas.length > 0 && <span className="pr-chip duvida"><CIcon name="cil-speech"/> dúvida do cliente</span>}
                        {i.em_revisao && <span className="pr-chip revisao">revisar resposta do cliente</span>}
                        {i.do_cliente && <span className="pr-chip cliente">cliente</span>}
                        {i.anexos > 0 && <span className="pr-chip"><CIcon name="cil-paperclip"/> {i.anexos}</span>}
                        {i.avulso && <span className="pr-chip">avulso</span>}
                        {agendado && <span className="pr-chip agendado" title={`${agendado.tipo_nome}: ${agendado.titulo}`}><CIcon name="cil-calendar"/> {quandoEvento(agendado.inicio)}</span>}
                    </span>
                </span>
                <CIcon name="cil-chevron-right" className="pr-item-seta"/>
            </button>
        );
    };

    const renderGrupos = (itens: ItemProcesso[]) => porGrupo(itens).map(([chave, lista], g) => (
        <div key={`${chave}-${g}`} className="pr-grupo">
            {chave && <div className="pr-grupo-nome">{chave}</div>}
            <div className="pr-grupo-itens">{lista.map(renderItem)}</div>
        </div>
    ));

    const renderResponsavelEtapa = (etapa: EtapaProcesso) => {
        const papel = papeis.get(etapa.papel_id);
        return (
            <div className="pr-etapa-resp">
                <span>Responsável: <strong>{papel?.pessoa || 'sem responsável'}</strong> <em>({papel?.nome})</em></span>
                {(podeAgirNaEtapa(etapa) || podeAgendar) && (
                    <span className="pr-etapa-acoes">
                        {podeAgirNaEtapa(etapa) && <button type="button" onClick={() => setEtapaModal({etapa, modo: 'transferir'})}>Transferir etapa</button>}
                        {processo.pode_gerir && <button type="button" onClick={() => setEtapaModal({etapa, modo: 'avulso'})}>+ Item</button>}
                        {podeAgendar && <button type="button" onClick={() => agendar({etapaId: etapa.id})}>Agendar</button>}
                    </span>
                )}
            </div>
        );
    };

    const filtros = (
        <div className="pr-filtros">
            {FILTROS.filter(([chave]) => !SO_COM_ITENS.includes(chave) || contagem[chave] > 0 || filtro === chave).map(([chave, rotulo]) => (
                <button type="button" key={chave} className={filtro === chave ? 'ativo' : ''} onClick={() => setFiltro(chave)}>
                    {rotulo} <span>{contagem[chave]}</span>
                </button>
            ))}
        </div>
    );

    /** Celular: etapa em sanfona. */
    const renderEtapaSanfona = (etapa: EtapaProcesso) => {
        const aberta = abertas.has(etapa.id) || filtro !== 'todos';
        const visiveis = etapa.itens.filter(passaFiltro);
        if (filtro !== 'todos' && visiveis.length === 0) {
            return null;
        }
        const situacao = SITUACAO_ETAPA[etapa.situacao] || SITUACAO_ETAPA.nao_iniciada;
        const alterna = () => {
            const novas = new Set(abertas);
            novas.has(etapa.id) ? novas.delete(etapa.id) : novas.add(etapa.id);
            setAbertas(novas);
        };
        return (
            <article key={etapa.id} className={`pr-etapa ${situacao.classe} ${etapa.id === processo.etapa_atual_id ? 'atual' : ''}`}>
                <button type="button" className="pr-etapa-cab" onClick={alterna}>
                    <span className="pr-etapa-numero">{etapa.situacao === 'concluida' ? <CIcon name="cil-check"/> : etapa.ordem}</span>
                    <span className="pr-etapa-texto">
                        <span className="pr-etapa-nome">{etapa.nome}</span>
                        <span className="pr-etapa-meta">
                            {situacao.rotulo} · {etapa.itens_resolvidos}/{etapa.itens_total} · até {dataCurta(etapa.previsao_fim)}
                            {etapa.desvio_dias_uteis !== 0 && ` (${textoDesvio(etapa.desvio_dias_uteis)})`}
                        </span>
                    </span>
                    <span className="pr-etapa-pct">{Math.round(etapa.percentual)}%</span>
                    <CIcon name={aberta ? 'cil-chevron-top' : 'cil-chevron-bottom'} className="pr-etapa-seta"/>
                </button>
                <div className="pr-barra fina"><span style={{width: `${etapa.percentual}%`}}/></div>
                {aberta && (
                    <div className="pr-etapa-corpo">
                        {renderResponsavelEtapa(etapa)}
                        {renderGrupos(visiveis)}
                    </div>
                )}
            </article>
        );
    };

    /** Eventos da etapa (os dos itens inclusive): os agendados e os ja realizados. */
    const renderAgendaEtapa = (etapa: EtapaProcesso) => {
        const lista = eventosDaEtapa(processo, etapa.id).filter(e => e.situacao !== SituacaoEvento.CANCELADO);
        if (!lista.length && !podeAgendar) return null;
        return (
            <section className="pr-bloco pr-etapa-agenda" aria-label="Agenda da etapa">
                <div className="pr-bloco-titulo">Agenda da etapa</div>
                {lista.length === 0 && <p className="pr-ajuda">Nada agendado nesta etapa.</p>}
                {lista.map(evento => (
                    <button type="button" key={evento.id} className={`pr-agenda-evento sit-evento-${evento.situacao} ${evento.situacao !== SituacaoEvento.AGENDADO ? 'passado' : ''}`}
                            onClick={() => abreEvento(evento)}>
                        <span className="pr-agenda-cor" style={{background: COR_EVENTO[evento.tipo]}}/>
                        <span>
                            <strong>{evento.titulo}</strong>
                            <em>{evento.tipo_nome} · {quandoEvento(evento.inicio)} · {evento.situacao_nome}</em>
                            {evento.item_titulo && evento.item_titulo !== evento.titulo && <em className="pr-agenda-item"><CIcon name="cil-task"/> {evento.item_titulo}</em>}
                        </span>
                    </button>
                ))}
                {podeAgendar && <button type="button" className="pr-link" onClick={() => agendar({etapaId: etapa.id})}>+ Agendar nesta etapa</button>}
            </section>
        );
    };

    /** Desktop: resumo da etapa na coluna da direita quando nenhum item esta aberto. */
    const renderResumoEtapa = (etapa: EtapaProcesso) => {
        const situacao = SITUACAO_ETAPA[etapa.situacao] || SITUACAO_ETAPA.nao_iniciada;
        const pendentes = etapa.itens.filter(i => !resolvido(i));
        return (
            <div className="pr-resumo-etapa">
                <div className="pr-resumo-etapa-cab">
                    <span className="pr-painel-etapa">Etapa {etapa.ordem}</span>
                    <h2>{etapa.nome}</h2>
                    <span className={`pr-chip etapa-${situacao.classe}`}>{situacao.rotulo}</span>
                </div>
                <div className="pr-painel-corpo">
                    {etapa.descricao && <p className="pr-orientacao">{etapa.descricao}</p>}
                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Progresso</div>
                        <div className="pr-progresso">
                            <div className="pr-barra"><span style={{width: `${etapa.percentual}%`}}/></div>
                            <strong>{Math.round(etapa.percentual)}%</strong>
                            <span>{etapa.itens_resolvidos}/{etapa.itens_total} itens</span>
                        </div>
                        {etapa.itens_atrasados > 0 && <span className="pr-chip atrasado">{etapa.itens_atrasados} atrasados</span>}
                    </section>
                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Cronograma</div>
                        <dl className="pr-datas">
                            <dt>Meta original</dt><dd>{dataBr(etapa.previsao_inicio_original)} → {dataBr(etapa.previsao_fim_original)}</dd>
                            <dt>Previsão atual</dt><dd>{dataBr(etapa.previsao_inicio)} → {dataBr(etapa.previsao_fim)}</dd>
                            <dt>Realizado</dt><dd>{etapa.data_inicio ? dataBr(etapa.data_inicio) : '—'} → {etapa.data_fim ? dataBr(etapa.data_fim) : '—'}</dd>
                            <dt>Desvio</dt><dd className={etapa.desvio_dias_uteis > 0 ? 'ruim' : ''}>{textoDesvio(etapa.desvio_dias_uteis)}</dd>
                            <dt>Duração</dt><dd>{etapa.duracao_dias_uteis} dias úteis</dd>
                        </dl>
                    </section>
                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Responsável</div>
                        {renderResponsavelEtapa(etapa)}
                    </section>
                    {pendentes.length > 0 && (
                        <section className="pr-bloco">
                            <div className="pr-bloco-titulo">Próximos prazos</div>
                            {pendentes.slice().sort((a, b) => a.prazo.localeCompare(b.prazo)).slice(0, 5).map(i => (
                                <button type="button" key={i.id} className="pr-proximo" onClick={() => setItemId(i.id)}>
                                    <span className={i.atrasado ? 'ruim' : ''}>{dataCurta(i.prazo)}</span>
                                    <span>{i.titulo}</span>
                                </button>
                            ))}
                        </section>
                    )}
                    {renderAgendaEtapa(etapa)}
                    <p className="pr-ajuda">Clique num item para responder.</p>
                </div>
            </div>
        );
    };

    /** Lista vazia: diz por que e oferece o caminho (todas as etapas, todos os itens). */
    const renderVazio = () => {
        const noProcesso = etapaSel ? conta(processo.etapas) : 0;
        return (
            <div className="pr-vazio pr-vazio-acoes">
                <span>{filtro === 'meus'
                    ? (etapaSel ? 'Nada seu pendente nesta etapa.' : 'Nada seu pendente neste processo.')
                    : (etapaSel ? `Nenhum item em “${rotuloFiltro}” nesta etapa.` : `Nenhum item em “${rotuloFiltro}”.`)}</span>
                {etapaSel && noProcesso > 0 && (
                    <button type="button" className="pr-link" onClick={() => alternaEtapa(null)}>Ver em todas as etapas ({noProcesso})</button>
                )}
                {filtro !== 'todos' && (
                    <button type="button" className="pr-link" onClick={() => setFiltro('todos')}>Ver todos os itens{etapaSel ? ' da etapa' : ''}</button>
                )}
            </div>
        );
    };

    /** Desktop, sem etapa marcada: o resumo do processo na coluna da direita. */
    const renderResumoGeral = () => {
        const proximos = processo.etapas.flatMap(e => e.itens).filter(i => !resolvido(i) && passaFiltro(i))
            .sort((a, b) => a.prazo.localeCompare(b.prazo)).slice(0, 6);
        const agenda = (processo.eventos || []).filter(e => e.situacao === SituacaoEvento.AGENDADO && e.inicio.slice(0, 10) >= hojeIso()).slice(0, 5);
        return (
            <div className="pr-resumo-etapa">
                <div className="pr-resumo-etapa-cab">
                    <span className="pr-painel-etapa">Processo</span>
                    <h2>Todas as etapas</h2>
                </div>
                <div className="pr-painel-corpo">
                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Progresso</div>
                        <div className="pr-progresso">
                            <div className="pr-barra"><span style={{width: `${processo.percentual}%`}}/></div>
                            <strong>{Math.round(processo.percentual)}%</strong>
                            <span>{processo.totais.resolvidos}/{processo.totais.itens} itens</span>
                        </div>
                    </section>
                    {proximos.length > 0 && (
                        <section className="pr-bloco">
                            <div className="pr-bloco-titulo">Próximos prazos{filtro !== 'todos' ? ` · ${rotuloFiltro}` : ''}</div>
                            {proximos.map(i => (
                                <button type="button" key={i.id} className="pr-proximo" onClick={() => setItemId(i.id)}>
                                    <span className={i.atrasado ? 'ruim' : ''}>{dataCurta(i.prazo)}</span>
                                    <span>{i.titulo}</span>
                                </button>
                            ))}
                        </section>
                    )}
                    {agenda.length > 0 && (
                        <section className="pr-bloco pr-etapa-agenda" aria-label="Agenda do processo">
                            <div className="pr-bloco-titulo">Agenda</div>
                            {agenda.map(evento => (
                                <button type="button" key={evento.id} className="pr-agenda-evento" onClick={() => abreEvento(evento)}>
                                    <span className="pr-agenda-cor" style={{background: COR_EVENTO[evento.tipo]}}/>
                                    <span>
                                        <strong>{evento.titulo}</strong>
                                        <em>{evento.tipo_nome} · {quandoEvento(evento.inicio)}</em>
                                    </span>
                                </button>
                            ))}
                        </section>
                    )}
                    <p className="pr-ajuda">Clique numa etapa para filtrar; clique de novo para ver todas.</p>
                </div>
            </div>
        );
    };

    const renderDesktop = () => {
        const porEtapa = processo.etapas
            .map(etapa => ({etapa, itens: etapa.itens.filter(passaFiltro)}))
            .filter(linha => linha.itens.length > 0);
        const daEtapa = etapaSel ? etapaSel.itens.filter(passaFiltro) : [];
        // No trilho, quantos itens do filtro cada etapa tem (com "Todos", o trilho fica so com o %).
        const contaRail = (n: number) => filtro !== 'todos' && (
            <span className={`pr-rail-conta ${n === 0 ? 'zero' : ''}`} title={`${rotuloFiltro}: ${n}`}>{n}</span>
        );
        return (
            <div className="pr-trabalho">
                <nav className="pr-rail">
                    <div className="pr-coluna-titulo">Etapas</div>
                    <button type="button" onClick={() => alternaEtapa(null)} className={`pr-rail-etapa todas ${etapaSel ? '' : 'selecionada'}`}
                            title="Itens de todas as etapas" aria-pressed={!etapaSel}>
                        <span className="pr-etapa-numero"><CIcon name="cil-layers"/></span>
                        <span className="pr-rail-texto">
                            <span className="pr-rail-nome">Todas as etapas</span>
                            <span className="pr-rail-meta">{processo.totais.resolvidos}/{processo.totais.itens} itens</span>
                        </span>
                        <span className="pr-rail-lado">{contaRail(conta(processo.etapas))}</span>
                    </button>
                    {processo.etapas.map(etapa => {
                        const situacao = SITUACAO_ETAPA[etapa.situacao] || SITUACAO_ETAPA.nao_iniciada;
                        const selecionada = etapa.id === etapaSel?.id;
                        return (
                            <button type="button" key={etapa.id} onClick={() => alternaEtapa(etapa)} aria-pressed={selecionada}
                                    title={selecionada ? 'Clique de novo para ver todas as etapas' : 'Filtrar por esta etapa'}
                                    className={`pr-rail-etapa ${situacao.classe} ${selecionada ? 'selecionada' : ''} ${etapa.id === processo.etapa_atual_id ? 'atual' : ''}`}>
                                <span className="pr-etapa-numero">{etapa.situacao === 'concluida' ? <CIcon name="cil-check"/> : etapa.ordem}</span>
                                <span className="pr-rail-texto">
                                    <span className="pr-rail-nome">{etapa.nome}</span>
                                    <span className="pr-rail-meta">{situacao.rotulo} · até {dataCurta(etapa.previsao_fim)}</span>
                                    <span className="pr-barra fina"><span style={{width: `${etapa.percentual}%`}}/></span>
                                </span>
                                <span className="pr-rail-lado">
                                    <span className="pr-rail-pct">{Math.round(etapa.percentual)}%</span>
                                    {contaRail(conta([etapa]))}
                                </span>
                            </button>
                        );
                    })}
                </nav>

                <section className="pr-lista">
                    <div className="pr-lista-topo">
                        {etapaSel ? (
                            <div className="pr-lista-titulo">
                                <span className="pr-etapa-numero grande">{etapaSel.ordem}</span>
                                <div>
                                    <h3>{etapaSel.nome}</h3>
                                    <span>{etapaSel.itens_resolvidos}/{etapaSel.itens_total} itens · até {dataBr(etapaSel.previsao_fim)}
                                        {etapaSel.desvio_dias_uteis !== 0 && ` (${textoDesvio(etapaSel.desvio_dias_uteis)})`}</span>
                                </div>
                                <button type="button" className="pr-lista-desmarcar" onClick={() => alternaEtapa(null)}
                                        title="Desmarcar a etapa: ver todas" aria-label="Desmarcar a etapa">
                                    <CIcon name="cil-x"/>
                                </button>
                            </div>
                        ) : (
                            <div className="pr-lista-titulo">
                                <span className="pr-etapa-numero grande todas"><CIcon name="cil-layers"/></span>
                                <div>
                                    <h3>Todas as etapas</h3>
                                    <span>{processo.totais.resolvidos}/{processo.totais.itens} itens · término {dataBr(processo.data_fim || processo.previsao_fim)}</span>
                                </div>
                            </div>
                        )}
                        {filtros}
                    </div>
                    <div className="pr-lista-corpo">
                        {etapaSel && renderGrupos(daEtapa)}
                        {!etapaSel && porEtapa.map(({etapa, itens}) => (
                            <div key={etapa.id} className="pr-lista-etapa">
                                <button type="button" className="pr-lista-etapa-nome" onClick={() => alternaEtapa(etapa)} title="Filtrar por esta etapa">{etapa.ordem}. {etapa.nome}</button>
                                {renderGrupos(itens)}
                            </div>
                        ))}
                        {(etapaSel ? daEtapa.length === 0 : porEtapa.length === 0) && renderVazio()}
                    </div>
                </section>

                <aside className="pr-lado">
                    {item ? (
                        <ItemPainel key={item.item.id} embutido processo={processo} etapa={item.etapa} item={item.item} papeis={papeis} usuarios={usuarios}
                                    ativo={ativo} versao={versaoHistorico} executar={executar} recarregar={recarregar} onFechar={() => setItemId(null)}
                                    ehCliente={papeis.get(item.item.papel_id)?.tipo === TIPO_PAPEL_CLIENTE}
                                    podeReprogramar={podeReprogramar(item.etapa)}
                                    eventos={eventosDoItem(processo, item.item.id)} podeAgendar={podeAgendar}
                                    onAgendar={() => agendar({itemId: item.item.id})} onAbrirEvento={abreEvento}/>
                    ) : (etapaSel ? renderResumoEtapa(etapaSel) : renderResumoGeral())}
                </aside>
            </div>
        );
    };

    const totais = processo.totais;
    return (
        <div className={`pr ${desktop ? 'pr-desktop' : ''}`}>
            <header className="pr-cab">
                <div className="pr-cab-linha">
                    <button type="button" className="pr-voltar" onClick={() => history.push(location.state?.voltar || '/processos')} title="Voltar"><CIcon name="cil-arrow-left"/></button>
                    <div className="pr-cab-titulo">
                        <h1>{processo.cliente.nome}</h1>
                        <span>
                            {processo.fluxo.codigo} · {processo.fluxo.nome} · GP {processo.gestor.nome}
                            {processo.fluxo_desatualizado && processo.pode_gerir && (
                                <a href={`#/processos/alterar/${processo.id}`} className="pr-cab-versao"
                                   title={`O processo segue a versão ${processo.fluxo.versao} do fluxo. Veja o que muda e atualize em Alterar.`}>
                                    fluxo v{processo.fluxo.versao_atual} disponível
                                </a>
                            )}
                        </span>
                    </div>
                    {desktop && (
                        <div className="pr-cab-progresso">
                            <div className="pr-progresso">
                                <div className="pr-barra"><span style={{width: `${processo.percentual}%`}}/></div>
                                <strong>{Math.round(processo.percentual)}%</strong>
                                <span>{totais.resolvidos}/{totais.itens} itens</span>
                            </div>
                        </div>
                    )}
                    <span className={`badge badge-${COR_SITUACAO[processo.situacao]} pr-situacao`}>{processo.situacao_nome}</span>
                    {participa && processo.situacao !== SituacaoProcesso.CANCELADO && (
                        <button type="button" className="pr-botao-portal" onClick={abrePortal} title="Ver o portal do cliente como ele vê (só leitura), em outra aba">
                            <CIcon name="cil-external-link"/> Portal do cliente
                        </button>
                    )}
                    {processo.pode_gerir && processo.transicoes.length > 0 && (
                        <button type="button" className="pr-botao-situacao" onClick={() => setSituacaoAberta(true)}>Situação</button>
                    )}
                </div>
                {!desktop && (
                    <div className="pr-progresso">
                        <div className="pr-barra"><span style={{width: `${processo.percentual}%`}}/></div>
                        <strong>{Math.round(processo.percentual)}%</strong>
                        <span>{totais.resolvidos}/{totais.itens} itens</span>
                    </div>
                )}
                <div className="pr-cab-rodape">
                    <div className="pr-indicadores">
                        <span title="Previsão de término; ao lado, o desvio em dias úteis em relação à meta original">
                            Término {dataBr(processo.data_fim || processo.previsao_fim)}
                            <em className={processo.desvio_dias_uteis > 0 ? 'ruim' : ''}> {textoDesvio(processo.desvio_dias_uteis)}</em>
                        </span>
                        {totais.duvidas > 0 && (
                            <button type="button" className="pr-chip duvida pr-chip-acao" onClick={() => { setAba('etapas'); setItemId(null); setEtapaId(null); setFiltro('duvidas'); }}
                                    title="Perguntas que o cliente mandou pelo portal e ninguém da equipe respondeu ainda">
                                <CIcon name="cil-speech"/> {totais.duvidas === 1 ? '1 dúvida do cliente' : `${totais.duvidas} dúvidas do cliente`}
                            </button>
                        )}
                        {totais.em_revisao > 0 && (
                            <button type="button" className="pr-chip revisao pr-chip-acao" onClick={() => { setAba('etapas'); setItemId(null); setEtapaId(null); setFiltro('revisar'); }}
                                    title="Respostas e arquivos que o cliente mandou pelo portal: revise e conclua">
                                {totais.em_revisao} para revisar
                            </button>
                        )}
                        <span className="pr-chip cliente">{totais.pendencias_cliente} do cliente</span>
                        <span className="pr-chip" title="Canal do Mattermost que recebe os avisos deste processo (escolhido na abertura)">
                            <CIcon name="cil-bell"/> {processo.webhook_mattermost ? processo.webhook_mattermost.nome : 'sem Mattermost'}
                        </span>
                        <span className="pr-chip">{totais.pendencias_equipe} da equipe</span>
                        {totais.atrasados > 0 && <span className="pr-chip atrasado">{totais.atrasados} atrasados</span>}
                        {totais.vencendo > 0 && <span className="pr-chip vencendo">{totais.vencendo} vencendo</span>}
                        {processo.situacao === SituacaoProcesso.CONGELADO && <span className="pr-chip congelado">congelado desde {dataBr(processo.congelado_em)}</span>}
                        {!ativo && processo.motivo_situacao && <span className="pr-motivo">{processo.situacao_nome}: {processo.motivo_situacao}</span>}
                    </div>
                    <nav className="pr-abas">
                        {ABAS.map(([chave, rotulo]) => (
                            <button type="button" key={chave} className={aba === chave ? 'ativa' : ''} onClick={() => setAba(chave)}>{rotulo}</button>
                        ))}
                    </nav>
                </div>
            </header>

            {aba === 'etapas' && desktop && renderDesktop()}
            {aba === 'etapas' && !desktop && (
                <main className="pr-conteudo">
                    {filtros}
                    {processo.etapas.map(renderEtapaSanfona)}
                    {filtro !== 'todos' && contagem[filtro] === 0 && <div className="pr-vazio">Nada aqui.</div>}
                </main>
            )}
            {aba !== 'etapas' && (
                <main className="pr-conteudo">
                    {aba === 'cronograma' && (
                        <CronogramaAba processo={processo} podeReprogramar={podeReprogramar} onReprogramar={setReprogramando} onAbrirItem={abreItem} onAbrirEvento={abreEvento}/>
                    )}
                    {aba === 'agenda' && (
                        <AgendaAba processo={processo} podeAgendar={podeAgendar} podeReprogramar={podeReprogramar} recarregar={recarregar}
                                   onAbrirItem={abreItem} onAgendar={agendar} onAbrirEvento={abreEvento}/>
                    )}
                    {aba === 'responsaveis' && <ResponsaveisAba processo={processo} usuarios={usuarios} executar={executar} recarregar={recarregar}/>}
                    {aba === 'historico' && <HistoricoAba processoId={processo.id} versao={versaoHistorico} executar={executar} podeComentar={true}/>}
                    {aba === 'documentos' && <DocumentosAba processo={processo} versao={versaoHistorico} executar={executar} recarregar={recarregar}/>}
                </main>
            )}

            {!desktop && item && (
                <ItemPainel processo={processo} etapa={item.etapa} item={item.item} papeis={papeis} usuarios={usuarios}
                            ativo={ativo} versao={versaoHistorico} executar={executar} recarregar={recarregar} onFechar={() => setItemId(null)}
                            ehCliente={papeis.get(item.item.papel_id)?.tipo === TIPO_PAPEL_CLIENTE}
                            podeReprogramar={podeReprogramar(item.etapa)}
                            eventos={eventosDoItem(processo, item.item.id)} podeAgendar={podeAgendar}
                            onAgendar={() => agendar({itemId: item.item.id})} onAbrirEvento={abreEvento}/>
            )}
            {agendamento && (
                <EventoModal key={agendamento.evento?.id ?? 'novo'} processo={processo} evento={agendamento.evento} usuarios={usuarios}
                             dataInicial={agendamento.data} inicioInicial={agendamento.inicio} fimInicial={agendamento.fim}
                             itemInicial={agendamento.itemId} etapaInicial={agendamento.etapaId}
                             onFechar={() => setAgendamento(null)} aoSalvar={recarregar} onAbrirItem={abreItem}/>
            )}
            {situacaoAberta && (
                <SituacaoModal processo={processo} executar={executar} onFechar={() => setSituacaoAberta(false)}/>
            )}
            {reprogramando && (
                <ReprogramarEtapaModal etapa={reprogramando} executar={executar} onFechar={() => setReprogramando(null)}/>
            )}
            {etapaModal && (
                <EtapaModal processo={processo} etapa={etapaModal.etapa} modo={etapaModal.modo} usuarios={usuarios}
                            executar={executar} onFechar={() => setEtapaModal(null)}/>
            )}
        </div>
    );
};

export default ProcessoTela;
